"use client";

import { useEffect, useState } from "react";
import type { CatalogItem } from "@/lib/airtable";

type Props = {
  item: Pick<CatalogItem, "id" | "itemName" | "pictureUrl" | "tagPrice" | "metal" | "stone" | "soldOut">;
};

const FAVORITES_STORAGE_KEY = "vault88-favorites";

export function FavoriteButton({ item }: Props) {
  const [isFavorited, setIsFavorited] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored) as Array<{ id: string }>;
      setIsFavorited(parsed.some((e) => e.id === item.id));
    } catch {
      // ignore
    }
  }, [item.id]);

  const toggle = () => {
    try {
      const stored = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
      const existing = stored ? (JSON.parse(stored) as Array<{ id: string }>) : [];
      const alreadyIn = existing.some((e) => e.id === item.id);
      const next = alreadyIn
        ? existing.filter((e) => e.id !== item.id)
        : [
            ...existing,
            {
              id: item.id,
              itemName: item.itemName,
              pictureUrl: item.pictureUrl,
              tagPrice: item.tagPrice,
              metal: item.metal,
              stone: item.stone,
              soldOut: item.soldOut
            }
          ];
      window.localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(next));
      setIsFavorited(!alreadyIn);
      window.dispatchEvent(
        new StorageEvent("storage", { key: FAVORITES_STORAGE_KEY, newValue: JSON.stringify(next) })
      );
    } catch {
      // ignore
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isFavorited ? "Remove from favorites" : "Save to favorites"}
      className="flex w-full items-center justify-center gap-2.5 rounded-sm border border-slate-200 px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-slate-700 transition hover:border-slate-400 hover:text-slate-900"
    >
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5 flex-shrink-0 transition-colors"
        aria-hidden="true"
        fill={isFavorited ? "#c4a668" : "none"}
        stroke={isFavorited ? "#c4a668" : "currentColor"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
      <span>{isFavorited ? "Favorited" : "Favorite"}</span>
    </button>
  );
}
